import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { dashboardApi } from "../../../../../services/dashboardApi";

const DETAIL_LABELS = {
  REJECT: "Reject Details",
  REWORK: "Rework Details",
  JOBCARDS: "Jobcard Details",
  MACHINES: "Machine Details",
  USERS: "User Details",
};

const TOTAL_LABELS = {
  REJECT: "Reject Quantity",
  REWORK: "Rework Quantity",
  JOBCARDS: "Total Jobcards",
  MACHINES: "Total Machines",
  USERS: "Total Users",
};

const formatNumber = (value) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(
    Number(value) || 0,
  );

const formatDateTime = (value) =>
  value ? moment(value).format("DD-MM-YYYY HH:mm") : "—";

const CodeAndName = ({ code, name }) => (
  <div className="department-detail-modal__identity">
    <strong>{name || code || "—"}</strong>
    {code && name && <span>{code}</span>}
  </div>
);

const Quantity = ({ value, type = "total" }) => (
  <span className={`department-detail-modal__quantity is-${type}`}>
    {formatNumber(value)}
  </span>
);

const Count = ({ value, label }) => (
  <div className="department-detail-modal__count">
    <strong>{formatNumber(value)}</strong>
    <span>{label}</span>
  </div>
);

const DepartmentProductionDetailModal = ({
  department,
  detailType,
  filters,
  onClose,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    dashboardApi
      .getProductionDepartmentDetails({
        filters,
        deptCode: department.deptCode,
        detailType,
        page,
        perPage: 25,
        search,
      })
      .then((response) => {
        if (!active) return;
        if (response?.status !== 200) {
          throw new Error(response?.message || "Unable to load department details.");
        }
        setData(response.data);
      })
      .catch((requestError) => {
        if (active) {
          setError(requestError.message || "Unable to load department details.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [department.deptCode, detailType, filters, page, search]);

  const title = DETAIL_LABELS[detailType] || "Department Details";
  const items = data?.items || [];
  const totalPages = Math.max(Number(data?.totalPages) || 0, 1);
  const range = useMemo(() => {
    if (!data?.totalItems) return "0 records";
    const start = (page - 1) * Number(data.perPage || 25) + 1;
    const end = Math.min(page * Number(data.perPage || 25), data.totalItems);
    return `${start}-${end} of ${formatNumber(data.totalItems)}`;
  }, [data, page]);

  const handleSearch = (event) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const renderHeader = () => {
    if (["REJECT", "REWORK"].includes(detailType)) {
      return (
        <tr>
          <th>Reason</th>
          {detailType === "REWORK" && <th>Rework Department</th>}
          <th>Quantity</th>
          <th>Jobcards</th>
          <th>Machines</th>
          <th>Users</th>
          <th>Entries</th>
          <th>Latest Activity</th>
        </tr>
      );
    }

    if (detailType === "JOBCARDS") {
      return (
        <tr>
          <th>Jobcard</th>
          <th>Size</th>
          <th>Customer</th>
          <th>Total Qty</th>
          <th>OK</th>
          <th>Reject</th>
          <th>Rework</th>
          <th>Machines</th>
          <th>Users</th>
          <th>Latest Activity</th>
        </tr>
      );
    }

    return (
      <tr>
        <th>{detailType === "MACHINES" ? "Machine" : "User"}</th>
        <th>Total Qty</th>
        <th>OK</th>
        <th>Reject</th>
        <th>Rework</th>
        <th>Jobcards</th>
        <th>{detailType === "MACHINES" ? "Users" : "Machines"}</th>
        <th>Latest Activity</th>
      </tr>
    );
  };

  const renderRow = (item, index) => {
    if (["REJECT", "REWORK"].includes(detailType)) {
      return (
        <tr key={`${item.reasonCode}-${item.reasonName}-${item.reworkDeptCode}-${index}`}>
          <td><CodeAndName code={item.reasonCode} name={item.reasonName} /></td>
          {detailType === "REWORK" && <td>{item.reworkDeptCode || "—"}</td>}
          <td><Quantity value={item.quantity} type={detailType.toLowerCase()} /></td>
          <td><Count value={item.jobcardCount} label="jobcards" /></td>
          <td><Count value={item.machineCount} label="machines" /></td>
          <td><Count value={item.userCount} label="users" /></td>
          <td>{formatNumber(item.recordCount)}</td>
          <td>{formatDateTime(item.latestActivity)}</td>
        </tr>
      );
    }

    if (detailType === "JOBCARDS") {
      return (
        <tr key={item.jobcard}>
          <td><span className="department-detail-modal__jobcard">{item.jobcard}</span></td>
          <td><CodeAndName code={item.sizeCode} name={item.sizeName} /></td>
          <td><CodeAndName code={item.customerCode} name={item.customerName} /></td>
          <td><Quantity value={item.totalQty} /></td>
          <td><Quantity value={item.okQty} type="ok" /></td>
          <td><Quantity value={item.rejectQty} type="reject" /></td>
          <td><Quantity value={item.reworkQty} type="rework" /></td>
          <td>{formatNumber(item.machineCount)}</td>
          <td>{formatNumber(item.userCount)}</td>
          <td>{formatDateTime(item.latestActivity)}</td>
        </tr>
      );
    }

    const code = detailType === "MACHINES" ? item.machineCode : item.userId;
    const name = detailType === "MACHINES" ? item.machineName : item.userName;
    return (
      <tr key={`${code}-${name}-${index}`}>
        <td><CodeAndName code={code} name={name} /></td>
        <td><Quantity value={item.totalQty} /></td>
        <td><Quantity value={item.okQty} type="ok" /></td>
        <td><Quantity value={item.rejectQty} type="reject" /></td>
        <td><Quantity value={item.reworkQty} type="rework" /></td>
        <td>{formatNumber(item.jobcardCount)}</td>
        <td>{formatNumber(detailType === "MACHINES" ? item.userCount : item.machineCount)}</td>
        <td>{formatDateTime(item.latestActivity)}</td>
      </tr>
    );
  };

  return (
    <div className="department-detail-modal" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="department-detail-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="department-detail-title">
        <header className="department-detail-modal__header">
          <div className="department-detail-modal__heading">
            <span className="department-detail-modal__icon" aria-hidden="true">
              <i className="bi bi-bar-chart-line" />
            </span>
            <div>
              <div className="department-detail-modal__eyebrow">
                {department.deptCode} · {department.deptName}
              </div>
              <h2 id="department-detail-title">{title}</h2>
              <p>Production activity within the selected dashboard period.</p>
            </div>
          </div>
          <button type="button" className="department-detail-modal__close" onClick={onClose} aria-label="Close details">
            <i className="bi bi-x-lg" />
          </button>
        </header>

        <div className="department-detail-modal__toolbar">
          <div className="department-detail-modal__stats">
            <div><span>{TOTAL_LABELS[detailType]}</span><strong>{formatNumber(data?.totalValue)}</strong></div>
            <div><span>{["REJECT", "REWORK"].includes(detailType) ? "Reason Groups" : "Detailed Records"}</span><strong>{formatNumber(data?.totalItems)}</strong></div>
            <div><span>Reporting Period</span><strong>{filters.fromDate} — {filters.toDate}</strong></div>
          </div>
          <form className="department-detail-modal__search" onSubmit={handleSearch}>
            <i className="bi bi-search" />
            <input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder={`Search ${title.toLowerCase()}`} aria-label={`Search ${title}`} />
            {searchInput && (
              <button type="button" className="department-detail-modal__clear" onClick={() => { setSearchInput(""); setSearch(""); setPage(1); }} aria-label="Clear search">
                <i className="bi bi-x" />
              </button>
            )}
            <button type="submit" className="department-detail-modal__search-button">Search</button>
          </form>
        </div>

        <div className="department-detail-modal__body">
          {loading ? (
            <div className="department-detail-modal__state"><span className="department-detail-modal__spinner" /><strong>Loading details…</strong></div>
          ) : error ? (
            <div className="department-detail-modal__state is-error"><i className="bi bi-exclamation-circle" /><strong>{error}</strong></div>
          ) : items.length === 0 ? (
            <div className="department-detail-modal__state"><i className="bi bi-inbox" /><strong>No matching production details</strong><span>Try another search or dashboard period.</span></div>
          ) : (
            <div className="department-detail-modal__table-wrapper">
              <table className="department-detail-modal__table">
                <thead>{renderHeader()}</thead>
                <tbody>{items.map(renderRow)}</tbody>
              </table>
            </div>
          )}
        </div>

        <footer className="department-detail-modal__footer">
          <span>Showing {range}</span>
          <div>
            <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((current) => Math.max(current - 1, 1))}>
              <i className="bi bi-chevron-left" /> Previous
            </button>
            <strong>Page {page} of {totalPages}</strong>
            <button type="button" disabled={page >= totalPages || loading} onClick={() => setPage((current) => current + 1)}>
              Next <i className="bi bi-chevron-right" />
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default DepartmentProductionDetailModal;
